import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { useEditorContext } from '@/hooks/useEditorContext';
import { useEditorProps } from '@/hooks/useEditorProps';
import { SyncScrollIframeComponent } from '@/components/UI/SyncScrollIframeComponent';
import { TestVariablesModal } from './TestVariablesModal';
import { SendTestEmailModal } from './SendTestEmailModal';
import {
	BasicType,
	formatDynamicTextValue,
	JsonToMjml,
	LEGACY_RAW_BLOCK_TYPE,
	normalizeMergeTagKey,
	parseDynamicTextHelperExpression,
} from '@bay-email/core';
import { stripMjmlDefaultHeadReset } from '@/utils/stripMjmlDefaultHeadReset';
import mjml from 'mjml-browser';
import { cloneDeep } from 'lodash';
import clsx from 'clsx';
import { SYNC_SCROLL_ELEMENT_CLASS_NAME } from '@/constants';
import { createPortal } from 'react-dom';
import { Button } from '@/components/UI/Button';
import { BsSendFill, BsDisplay, BsPhone } from 'react-icons/bs';
import kvstore from '~/services/kvstore.js';
import companyService from '~/services/companyService';
import BaySpinner from '@bay-ui/BaySpinner';
import './index.scss';

const GOOGLE_FONT_NAME_MAP: Record<string, string> = {
	roboto: 'Roboto',
	'open sans': 'Open Sans',
	lato: 'Lato',
	montserrat: 'Montserrat',
	poppins: 'Poppins',
	raleway: 'Raleway',
	oswald: 'Oswald',
	'pt sans': 'PT Sans',
	merriweather: 'Merriweather',
	nunito: 'Nunito',
	ubuntu: 'Ubuntu',
	'playfair display': 'Playfair Display',
	'noto sans': 'Noto Sans',
	mukta: 'Mukta',
	rubik: 'Rubik',
	'work sans': 'Work Sans',
	lora: 'Lora',
	'fira sans': 'Fira Sans',
	'nunito sans': 'Nunito Sans',
	quicksand: 'Quicksand',
	karla: 'Karla',
	hind: 'Hind',
	barlow: 'Barlow',
	'titillium web': 'Titillium Web',
	oxygen: 'Oxygen',
	arimo: 'Arimo',
	mulish: 'Mulish',
	'source sans pro': 'Source Sans Pro',
	'crimson text': 'Crimson Text',
	cabin: 'Cabin',
	anton: 'Anton',
	'bebas neue': 'Bebas Neue',
	teko: 'Teko',
	rajdhani: 'Rajdhani',
	'russo one': 'Russo One',
	orbitron: 'Orbitron',
	'archivo narrow': 'Archivo Narrow',
	'roboto condensed': 'Roboto Condensed',
	'barlow condensed': 'Barlow Condensed',
	'pt sans narrow': 'PT Sans Narrow',
	'ibm plex sans condensed': 'IBM Plex Sans Condensed',
	'libre franklin': 'Libre Franklin',
	'libre baskerville': 'Libre Baskerville',
	'abril fatface': 'Abril Fatface',
	heebo: 'Heebo',
	'dancing script': 'Dancing Script',
	manrope: 'Manrope',
	comfortaa: 'Comfortaa',
	pacifico: 'Pacifico',
	'exo 2': 'Exo 2',
	'fjalla one': 'Fjalla One',
	'zilla slab': 'Zilla Slab',
	'yanone kaffeesatz': 'Yanone Kaffeesatz',
	inter: 'Inter',
	'dm sans': 'DM Sans',
	'dm serif display': 'DM Serif Display',
	'ibm plex sans': 'IBM Plex Sans',
	'pt serif': 'PT Serif',
	urbanist: 'Urbanist',
	'archivo black': 'Archivo Black',
	bitter: 'Bitter',
	cinzel: 'Cinzel',
	fredoka: 'Fredoka',
	lobster: 'Lobster',
};

function normalizeFontName(fontName: string) {
	return fontName
		.replace(/&quot;|&#34;|&#x22;/gi, '"')
		.replace(/&#39;|&#x27;/gi, "'")
		.replace(/\s*!important\s*$/i, '')
		.replace(/^['"]+|['"]+$/g, '')
		.trim()
		.toLowerCase();
}

const GENERIC_FONT_FAMILIES = new Set([
	'serif',
	'sans-serif',
	'monospace',
	'cursive',
	'fantasy',
	'system-ui',
	'emoji',
	'math',
	'fangsong',
	'ui-serif',
	'ui-sans-serif',
	'ui-monospace',
	'ui-rounded',
]);

function parseFontStack(fontFamilyValue: string) {
	return String(fontFamilyValue || '')
		.split(',')
		.map(part => normalizeFontName(part))
		.filter(Boolean);
}

interface GoogleFontSupportData {
	googleFonts: string[];
	missingFallbackFonts: string[];
}

function isFontFamilyKey(key: string) {
	const normalizedKey = key.toLowerCase();
	return normalizedKey === 'font-family' || normalizedKey === 'fontfamily';
}

function isFontRelevantStringKey(key?: string) {
	if (!key) return false;
	const normalizedKey = key.toLowerCase();
	return (
		normalizedKey.includes('font') ||
		normalizedKey.includes('style') ||
		normalizedKey.includes('content') ||
		normalizedKey.includes('html') ||
		normalizedKey.includes('css')
	);
}

function createGoogleFontSupportCollector() {
	const googleFonts = new Set<string>();
	const missingFallbackFonts = new Set<string>();
	const addGoogleFontByNormalizedName = (normalizedFontName: string) => {
		const displayName = GOOGLE_FONT_NAME_MAP[normalizedFontName];
		if (displayName) {
			googleFonts.add(displayName);
		}
	};

	const consumeFontStack = (fontFamilyValue: string) => {
		const stackParts = parseFontStack(fontFamilyValue);
		if (!stackParts.length) {
			return;
		}

		const primaryFont = stackParts[0];
		if (primaryFont && GOOGLE_FONT_NAME_MAP[primaryFont]) {
			addGoogleFontByNormalizedName(primaryFont);

			const hasNonGenericFallback = stackParts
				.slice(1)
				.some(font => !GENERIC_FONT_FAMILIES.has(font));

			if (!hasNonGenericFallback) {
				missingFallbackFonts.add(GOOGLE_FONT_NAME_MAP[primaryFont]);
			}
		}
	};

	const toResult = (): GoogleFontSupportData => ({
		googleFonts: Array.from(googleFonts).sort((a, b) => a.localeCompare(b)),
		missingFallbackFonts: Array.from(missingFallbackFonts).sort((a, b) =>
			a.localeCompare(b),
		),
	});

	return {
		consumeFontStack,
		addGoogleFontByNormalizedName,
		toResult,
	};
}

function extractFontFamiliesFromHtmlString(content: string) {
	const matches: string[] = [];
	if (!content || typeof content !== 'string') {
		return matches;
	}

	const normalizedContent = content
		.replace(/&quot;|&#34;|&#x22;/gi, '"')
		.replace(/&#39;|&#x27;|&apos;/gi, "'");

	const fontFamilyRegex = /font-family\s*:\s*([^;}{]+)/gi;
	let match = fontFamilyRegex.exec(normalizedContent);
	while (match) {
		if (match[1]) {
			matches.push(match[1].trim());
		}
		match = fontFamilyRegex.exec(normalizedContent);
	}

	return matches;
}

function collectGoogleFontSupportDataFromPageData(pageData: any): GoogleFontSupportData {
	const collector = createGoogleFontSupportCollector();
	const visited = new WeakSet<object>();

	const walk = (node: unknown, parentKey?: string) => {
		if (node == null) {
			return;
		}

		if (typeof node === 'string') {
			extractFontFamiliesFromHtmlString(node).forEach(fontFamily => {
				collector.consumeFontStack(fontFamily);
			});

			if (parentKey && isFontFamilyKey(parentKey)) {
				collector.consumeFontStack(node);
			} else if (isFontRelevantStringKey(parentKey)) {
				const normalizedFontValue = normalizeFontName(node);
				if (GOOGLE_FONT_NAME_MAP[normalizedFontValue]) {
					collector.addGoogleFontByNormalizedName(normalizedFontValue);
				}
			}

			return;
		}

		if (Array.isArray(node)) {
			node.forEach(item => walk(item, parentKey));
			return;
		}

		if (typeof node !== 'object') {
			return;
		}

		if (visited.has(node as object)) {
			return;
		}

		visited.add(node as object);
		const objectNode = node as Record<string, unknown>;

		const fontFamilyValue = objectNode['font-family'];
		if (typeof fontFamilyValue === 'string') {
			collector.consumeFontStack(fontFamilyValue);
		}

		const camelFontFamilyValue = objectNode.fontFamily;
		if (typeof camelFontFamilyValue === 'string') {
			collector.consumeFontStack(camelFontFamilyValue);
		}

		Object.entries(objectNode).forEach(([key, value]) => walk(value, key));
	};

	walk(pageData);

	return collector.toResult();
}

function getGoogleFontSupportData(
	pageData: any,
	previewHtml?: string,
): GoogleFontSupportData {
	const jsonData = collectGoogleFontSupportDataFromPageData(pageData);
	if (jsonData.googleFonts.length > 0) {
		return jsonData;
	}

	if (previewHtml) {
		const fallbackCollector = createGoogleFontSupportCollector();
		extractFontFamiliesFromHtmlString(previewHtml).forEach(fontFamily => {
			fallbackCollector.consumeFontStack(fontFamily);
		});
		const fallbackData = fallbackCollector.toResult();
		if (fallbackData.googleFonts.length > 0) {
			return fallbackData;
		}
	}

	return jsonData;
}

/**
 * PreviewModal - Generates email preview with test data on-demand
 *
 * NOTE: This component no longer uses PreviewEmailProvider to avoid performance issues.
 * It generates its own preview HTML using the customPreviewData useMemo hook, which only
 * runs when the modal is visible. This prevents excessive MJML compilation in the background.
 */

const SIGNATURE_BLOCK_LOGO_URL =
	'https://bay.cdn.bkat.io/cust/bay_demo/images/logos/cbaq01.png?v=64958';
const USER_LOGO_DARK_TEST_URL =
	'https://bay.cdn.bkat.io/cust/bay_demo/images/logos/cbaq01.png?v=64958';
const USER_LOGO_LIGHT_TEST_URL =
	'https://staging-bay.cdn.bkat.io/cust/bay_staging/images/logos/realistiq.20260603133246600.light.png?v=18745';
const OFFICE_LOGO_DARK_TEST_URL =
	'https://bay.cdn.bkat.io/cust/bay_demo/images/logos/cbaq01.png?v=64958';
const OFFICE_LOGO_LIGHT_TEST_URL =
	'https://staging-bay.cdn.bkat.io/cust/bay_staging/images/logos/realistiq.20260603133246600.light.png?v=18745';


// Helper function to generate sample values based on field names
function generateSampleValue(fieldKey: string): string {
	const key = fieldKey.toLowerCase();
	// NOTE: brand colours are intentionally NOT given a sample value here. They are a
	// dynamic, account-specific value (e.g. {{user.brandPrimaryColor}}) that must
	// resolve to the user's REAL brand hex from the merge tags / dataSource, exactly
	// like the editor canvas does. A hardcoded fallback (formerly #000000 / #eb4034)
	// made dynamic colours render black/placebo-red in the preview when the real value
	// wasn't threaded through. Seeding is handled by getBrandColorOverridesFromMergeTags below.

	if (key.includes('name')) return 'Sample Name';
	if (key.includes('email')) return 'sample@example.com';
	if (key.includes('phone')) return '(555) 123-4567';
	if (key.includes('address')) return '123 Main St';
	if (key.includes('city')) return 'Sample City';
	if (key.includes('state')) return 'CA';
	if (key.includes('zip') || key.includes('postal')) return '94105';
	if (key.includes('title')) return 'Sample Title';
	if (key.includes('officelogolighturl')) return OFFICE_LOGO_LIGHT_TEST_URL;
	if (key.includes('officelogodarkurl')) return OFFICE_LOGO_DARK_TEST_URL;
	if (key.includes('logolighturl')) return USER_LOGO_LIGHT_TEST_URL;
	if (key.includes('logodarkurl')) return USER_LOGO_DARK_TEST_URL;
	if (key.includes('logo')) return SIGNATURE_BLOCK_LOGO_URL;
	if (key.includes('company')) return 'Tech Corp Inc.';
	if (key.includes('price')) return '$299.99';
	if (key.includes('image') || key.includes('photo'))
		return 'https://placehold.co/300x200';
	if (key.includes('url') || key.includes('link') || key.includes('website'))
		return 'https://example.com';
	if (key.includes('description'))
		return 'This is a sample description for testing purposes.';
	if (key.includes('date')) return new Date().toLocaleDateString();

	// Default fallback
	return `Sample ${fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1)}`;
}

/**
 * Pull the user's real dynamic brand colours out of the merge tags so the preview can
 * resolve {{user.brandPrimaryColor}} / {{user.brandSecondaryColor}} to the SAME hex the
 * editor canvas shows.
 *
 * The brand colours live on `mergeTags.user.brand{Primary,Secondary}Color` (injected by
 * the host as `realBrandOverrides`). They are NOT hand-editable test variables, so they
 * must be seeded straight from the merge tags into the MJML dataSource — otherwise the
 * tag resolves to nothing and the block renders the raw `{{...}}` (i.e. black).
 */
function getBrandColorOverridesFromMergeTags(
	previewMergeTags: Record<string, any> | undefined,
): { user: Record<string, string> } | undefined {
	const user = (previewMergeTags?.user || {}) as Record<string, unknown>;
	const overrides: Record<string, string> = {};

	const primary = user.brandPrimaryColor;
	const secondary = user.brandSecondaryColor;
	if (typeof primary === 'string' && primary.trim()) {
		overrides.brandPrimaryColor = primary.trim();
	}
	if (typeof secondary === 'string' && secondary.trim()) {
		overrides.brandSecondaryColor = secondary.trim();
	}

	return Object.keys(overrides).length ? { user: overrides } : undefined;
}

const KVSTORE_COMPONENT_KEY = 'emailEditorPreviewTestVariables';
const KVSTORE_SCHEMA_VERSION = 1;
const PREVIEW_TEST_VALUES_UPDATED_EVENT = 'email-editor-preview-test-values-updated';
const DOWNSTREAM_RESOLVED_MERGE_TAG_LEAF_KEYS = new Set([
	'unsubscribelink',
	'campaigndisclaimer',
]);
const PREVIEW_ONLY_DOWNSTREAM_MERGE_TAG_VALUES: Record<string, string> = {
	unsubscribelink: '{{unsubscribeLink}}',
	campaigndisclaimer: '{{campaignDisclaimer}}',
};

function isDownstreamResolvedMergeTagKey(key: string): boolean {
	const normalizedKey = String(key || '').trim();
	if (!normalizedKey) return false;

	const leafKey = normalizedKey.split('.').pop() || normalizedKey;
	return DOWNSTREAM_RESOLVED_MERGE_TAG_LEAF_KEYS.has(leafKey.toLowerCase());
}

function sanitizeDownstreamResolvedTestValues(
	values: Record<string, string>,
): Record<string, string> {
	const sanitized: Record<string, string> = {};

	Object.entries(values || {}).forEach(([key, value]) => {
		if (isDownstreamResolvedMergeTagKey(key)) return;
		sanitized[key] = value;
	});

	return sanitized;
}

function getPreviewOnlyDownstreamMergeTagValue(key: string): string | undefined {
	const normalizedKey = normalizeMergeTagKey(String(key || '').trim());
	const leafKey = normalizedKey.split('.').pop() || normalizedKey;

	return (
		PREVIEW_ONLY_DOWNSTREAM_MERGE_TAG_VALUES[normalizeFieldName(normalizedKey)] ??
		PREVIEW_ONLY_DOWNSTREAM_MERGE_TAG_VALUES[normalizeFieldName(leafKey)]
	);
}

function isUsableDownstreamPreviewValue(value: unknown): value is string {
	if (typeof value !== 'string') return false;
	const trimmed = value.trim();
	if (!trimmed) return false;

	// Ignore unresolved merge-tag tokens such as {{unsubscribeLink}}.
	if (/^\{\{[\s\S]+\}\}$/.test(trimmed)) return false;

	return true;
}

function normalizeFieldName(fieldName: string): string {
	return fieldName.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function normalizePersistedTestValue(value: string): string {
	return value.replace(/https?:\/\/via\.placeholder\.com\//gi, 'https://placehold.co/');
}

function extractPersistedValues(rawData: any): {
	values: Record<string, string>;
	wasSanitized: boolean;
} {
	if (!rawData || typeof rawData !== 'object') {
		return {
			values: {},
			wasSanitized: false,
		};
	}

	const sourceData =
		rawData?.values && typeof rawData.values === 'object' ? rawData.values : rawData;

	const normalized: Record<string, string> = {};
	let wasSanitized = false;
	Object.entries(sourceData).forEach(([key, value]) => {
		if (value !== undefined && value !== null) {
			const originalValue = String(value);
			const sanitizedValue = normalizePersistedTestValue(originalValue);
			normalized[key] = sanitizedValue;
			if (sanitizedValue !== originalValue) {
				wasSanitized = true;
			}
		}
	});

	return {
		values: normalized,
		wasSanitized,
	};
}

function mergeWithCurrentMergeTags(
	defaults: Record<string, string>,
	persisted: Record<string, string>,
): Record<string, string> {
	if (!Object.keys(defaults).length) return persisted;

	const uniqueLeafToKeyMap: Record<string, string | null> = {};
	Object.keys(defaults).forEach(fullKey => {
		const leafName = normalizeFieldName(fullKey.split('.').pop() || fullKey);
		if (!leafName) return;

		if (Object.prototype.hasOwnProperty.call(uniqueLeafToKeyMap, leafName)) {
			uniqueLeafToKeyMap[leafName] = null;
		} else {
			uniqueLeafToKeyMap[leafName] = fullKey;
		}
	});

	const resolvedPersisted: Record<string, string> = {};

	Object.entries(persisted).forEach(([persistedKey, persistedValue]) => {
		if (Object.prototype.hasOwnProperty.call(defaults, persistedKey)) {
			resolvedPersisted[persistedKey] = persistedValue;
			return;
		}

		const leafName = normalizeFieldName(persistedKey.split('.').pop() || persistedKey);
		const mappedCurrentKey = uniqueLeafToKeyMap[leafName];

		if (mappedCurrentKey) {
			resolvedPersisted[mappedCurrentKey] = persistedValue;
			return;
		}

		resolvedPersisted[persistedKey] = persistedValue;
	});

	return {
		...defaults,
		...resolvedPersisted,
	};
}
function buildPreviewIframeHtml(html: string, viewMode: 'desktop' | 'mobile') {
	if (!html) return '';

	const doc = new DOMParser().parseFromString(html, 'text/html');
	const wrapper = doc.createElement('div');
	wrapper.className = `preview-container ${SYNC_SCROLL_ELEMENT_CLASS_NAME}`;
	wrapper.setAttribute(
		'style',
		[
			'height:100%',
			'margin:auto',
			`padding-left:${viewMode === 'mobile' ? 5 : 20}px`,
			`padding-right:${viewMode === 'mobile' ? 5 : 20}px`,
			'padding-top:20px',
			'padding-bottom:40px',
			'box-sizing:border-box',
		].join(';'),
	);

	while (doc.body.firstChild) {
		wrapper.appendChild(doc.body.firstChild);
	}

	doc.body.setAttribute('style', 'margin:0;background:transparent;');
	doc.body.appendChild(wrapper);

	return `<!doctype html>${doc.documentElement.outerHTML}`;
}

function enforceInheritedColorOnUnsubscribeAnchors(html: string): string {
	if (!html) return html;

	return html.replace(/<a\b[^>]*>/gi, anchorTag => {
		const hrefMatch = anchorTag.match(/\shref=(['"])(.*?)\1/i);
		if (!hrefMatch) return anchorTag;

		const hrefValue = String(hrefMatch[2] || '');
		if (!/unsubscribe|unsubscribelink|legal_compliance\.unsubscribeLink/i.test(hrefValue)) {
			return anchorTag;
		}

		const styleMatch = anchorTag.match(/\sstyle=(['"])(.*?)\1/i);
		if (!styleMatch) {
			return anchorTag.replace(/>$/, ' style="color: inherit !important;">');
		}

		const quote = styleMatch[1] || '"';
		const styleValue = String(styleMatch[2] || '');
		if (/color\s*:\s*inherit/i.test(styleValue)) {
			return anchorTag;
		}

		const separator = styleValue.trim().endsWith(';') || styleValue.trim() === '' ? '' : ';';
		const nextStyleValue = `${styleValue}${separator} color: inherit !important;`;
		return anchorTag.replace(
			/\sstyle=(['"])(.*?)\1/i,
			` style=${quote}${nextStyleValue}${quote}`,
		);
	});
}

function isSingleHtmlBlockContent(root: any) {
	const layoutTypes = new Set([
		BasicType.TEMPLATE,
		BasicType.PAGE,
		BasicType.WRAPPER,
		BasicType.SECTION,
		BasicType.COLUMN,
		BasicType.GROUP,
	]);

	const contentBlockTypes: string[] = [];
	const stack = [root];

	while (stack.length) {
		const current = stack.pop();
		if (!current || typeof current !== 'object') continue;

		const rawType = (current as any)?.type;
		const type = typeof rawType === 'string' ? rawType.toLowerCase() : '';

		if (type && !layoutTypes.has(type as BasicType)) {
			contentBlockTypes.push(type);
		}

		const children = (current as any)?.children;
		if (Array.isArray(children) && children.length) {
			for (const child of children) {
				stack.push(child);
			}
		}
	}

	return (
		contentBlockTypes.length === 1 &&
		(contentBlockTypes[0] === BasicType.RAW ||
			contentBlockTypes[0] === LEGACY_RAW_BLOCK_TYPE)
	);
}

// Fallback sample images for listings and users, used when a block has no real selection yet.
const SAMPLE_LISTING_IMAGE_URL = 'https://placehold.co/600x400/cccccc/666666?text=Sample+Listing';

// Helper function to generate sample listing data for a given index, using the mockMergeTags
function generateSampleListingItem(
	index: number,
	mockMergeTags: Record<string, string>,
): Record<string, any> {
	// Reuse the same curated mock dataset as the merge-tag preview, instead of a disconnected
	// generic placeholder, so a mocked Listing block matches the mocked {{listing.*}} tags.
	const address = mockMergeTags['listing.address'] || `${100 + index * 5} Sample Street`;
	const imageKey = `listing.image_data_${index % 10}`;

	return {
		id: `sample-${index + 1}`,
		title: address,
		address,
		city: mockMergeTags['listing.city'] || 'Sampletown',
		stateProvince: mockMergeTags['listing.state'] || 'CA',
		postalCode: mockMergeTags['listing.zip'] || '90210',
		list_price: mockMergeTags['listing.listPrice'] || '550,000',
		beds: Number(mockMergeTags['listing.beds']) || 3,
		baths: Number(mockMergeTags['listing.bathsTotal']) || 2,
		sq_foot: mockMergeTags['listing.squareFootage'] || mockMergeTags['listing.sqFoot'] || 1800,
		image_data: [mockMergeTags[imageKey] || SAMPLE_LISTING_IMAGE_URL],
	};
}

// Array-shaped fields that get the same treatment: replace every item with a generated
// sample, sized by the existing array length or the block's maxCount (falling back to 6).
const MOCK_ARRAY_FIELD_GENERATORS: Array<
	[string, (index: number, mockMergeTags: Record<string, string>) => Record<string, any>]
> = [
	['selectedListings', generateSampleListingItem],
	['criteriaResults', generateSampleListingItem],
];

/**
 * Fills mock data ONLY where a block has no real selection yet, so real selections still
 * show in the on-screen preview.
 */
function fillUnselectedBlocksWithMockPreviewData(
	node: any,
	mockMergeTags: Record<string, string>,
): any {
	if (!node || typeof node !== 'object') return node;

	const clone: any = Array.isArray(node) ? [...node] : { ...node };

	if (Array.isArray(clone.children)) {
		clone.children = clone.children.map((child: any) =>
			fillUnselectedBlocksWithMockPreviewData(child, mockMergeTags),
		);
	}

	const value = clone?.data?.value;
	if (value && typeof value === 'object') {
		const nextValue: Record<string, any> = { ...value };
		let changed = false;

		// SingleListing block: `selectedListing` is only present once a listing has actually
		// been picked. Its sibling fields (`selectedListingId`/`placeholderImage`) identify the
		// block even before a real selection exists.
		const isSingleListingBlock =
			'placeholderImage' in nextValue || 'selectedListingId' in nextValue;
		if (
			isSingleListingBlock &&
			(!nextValue.selectedListing || typeof nextValue.selectedListing !== 'object')
		) {
			nextValue.selectedListing = generateSampleListingItem(0, mockMergeTags);
			changed = true;
		}

		MOCK_ARRAY_FIELD_GENERATORS.forEach(([field, generator]) => {
			if (Array.isArray(nextValue[field]) && nextValue[field].length === 0) {
				const sampleCount = Number(nextValue.maxCount) || 6;
				nextValue[field] = Array.from({ length: sampleCount }, (_: unknown, i: number) =>
					generator(i, mockMergeTags),
				);
				changed = true;
			}
		});

		if (changed) {
			clone.data = { ...clone.data, value: nextValue };
		}
	}

	return clone;
}

// Module-scoped: the disclaimer never changes within a session, so cache it across mounts/opens.
let campaignDisclaimerCache: string | null | undefined;
let campaignDisclaimerRequest: Promise<string | null> | null = null;

function fetchCampaignDisclaimerOnce(): Promise<string | null> {
	if (campaignDisclaimerCache !== undefined) {
		return Promise.resolve(campaignDisclaimerCache);
	}

	if (!campaignDisclaimerRequest) {
		campaignDisclaimerRequest = companyService
			.getCampaignDisclaimer()
			.then((response: any) => {
				const resolved =
					response?.STATUS === 0 && response?.campaignDisclaimer
						? (response.campaignDisclaimer as string)
						: null;
				campaignDisclaimerCache = resolved;
				return resolved;
			})
			.catch((error: any) => {
				console.error('[PreviewModal] Failed to load campaign disclaimer:', error);
				// Don't cache a failure — allow the next open to retry.
				campaignDisclaimerRequest = null;
				return null;
			});
	}

	return campaignDisclaimerRequest;
}

export interface PreviewModalProps {
	isVisible: boolean;
	onClose: () => void;
	onSendTestEmail?: (emails: string[], html: string, subject: string) => Promise<void>;
	userEmail?: string;
	userName?: string;
	userId?: string | number | null;
}

export function PreviewModal({
	isVisible,
	onClose,
	onSendTestEmail,
	userEmail,
	userName,
	userId,
}: PreviewModalProps) {
	const { pageData, formState } = useEditorContext();
	const {
		mergeTags,
		baseMergeTags,
		previewInjectData,
		isDesignAdmin,
		realUserProfile,
		realUserEmailSignature,
		blockMode,
		recipientPreview,
	} = useEditorProps();
	// Real disclaimer text, fetched from the backend; design admins keep the raw tag.
	const [realCampaignDisclaimer, setRealCampaignDisclaimer] = useState<string | null>(null);
	const [hasLoadedCampaignDisclaimer, setHasLoadedCampaignDisclaimer] = useState(false);
	// Overlay onto legal_compliance so resolveDownstreamValueFromMergeTags picks it up like unsubscribeLink.
	const previewMergeTags = useMemo(() => {
		const base = previewInjectData || mergeTags;
		if (isDesignAdmin || !realCampaignDisclaimer) return base;
		return {
			...base,
			legal_compliance: {
				...(base?.legal_compliance || {}),
				campaignDisclaimer: realCampaignDisclaimer,
			},
		};
	}, [previewInjectData, mergeTags, isDesignAdmin, realCampaignDisclaimer]);
	// Pristine defaults (no kvstore overrides) for the Test Variables "Reset" action.
	// Falls back to the merged tags if the host didn't provide a base.
	const resetMergeTags = baseMergeTags || previewMergeTags;
	const kvstoreUserId: string | number | null = userId ?? null;
	const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');
	const [isTestVariablesModalVisible, setIsTestVariablesModalVisible] = useState(false);
	const [isSendTestModalVisible, setIsSendTestModalVisible] = useState(false);
	const [isHelpPopoverVisible, setIsHelpPopoverVisible] = useState(false);
	const [previewIframeWindow, setPreviewIframeWindow] = useState<Window | null>(null);
	const [testValues, setTestValues] = useState<Record<string, string>>({});
	const [hasLoadedTestValues, setHasLoadedTestValues] = useState(false);

	// Initialize default test values based on merge tags
	const defaultTestValues = useMemo(() => {
		if (!previewMergeTags) return {};

		const defaults: Record<string, string> = {};
		Object.entries(previewMergeTags).forEach(([groupKey, groupData]) => {
			if (typeof groupData === 'object' && groupData !== null) {
				Object.entries(groupData).forEach(([fieldKey, fieldValue]) => {
					const fullKey = `${groupKey}.${fieldKey}`;
					if (isDownstreamResolvedMergeTagKey(fullKey)) return;
					// Use actual values from mergeTags, fallback to generated values if empty
					const defaultValue = fieldValue
						? String(fieldValue)
						: generateSampleValue(fieldKey);
					defaults[fullKey] = normalizePersistedTestValue(defaultValue);
				});
			}
		});

		return defaults;
	}, [previewMergeTags]);

	function resolveDownstreamValueFromMergeTags(key: string): string | undefined {
		if (!previewMergeTags || typeof previewMergeTags !== 'object') return undefined;

		const rawKey = String(key || '').trim();
		const normalizedKey = normalizeMergeTagKey(rawKey);
		const leafKey = normalizedKey.split('.').pop() || normalizedKey;
		const keysToTry = [
			rawKey,
			normalizedKey,
			leafKey,
			`legal_compliance.${leafKey}`,
		];

		for (const candidate of keysToTry) {
			if (!candidate) continue;

			const candidateParts = candidate.split('.').filter(Boolean);
			let current: unknown = previewMergeTags;

			for (const part of candidateParts) {
				if (typeof current !== 'object' || current === null) {
					current = undefined;
					break;
				}

				current = (current as Record<string, unknown>)[part];
			}

			if (isUsableDownstreamPreviewValue(current)) {
				return current.trim();
			}
		}

		return undefined;
	}

	// Load persisted test values and gracefully merge with current merge tags.
	useEffect(() => {
		if (!isVisible || hasLoadedTestValues) return;

		if (Object.keys(defaultTestValues).length === 0) {
			return;
		}

		const loadTestValues = async () => {
			try {
				let persistedValues: Record<string, string> = {};
				let persistedValuesWereSanitized = false;

				if (kvstoreUserId) {
					const response = await kvstore.read({
						component: KVSTORE_COMPONENT_KEY,
						userId: kvstoreUserId,
					});

					const extractedPersistedValues = extractPersistedValues(response?.data?.val);
					persistedValues = extractedPersistedValues.values;
					persistedValuesWereSanitized = extractedPersistedValues.wasSanitized;

					if (persistedValuesWereSanitized) {
						await kvstore.update({
							component: KVSTORE_COMPONENT_KEY,
							userId: kvstoreUserId,
							data: {
								version: KVSTORE_SCHEMA_VERSION,
								updatedAt: new Date().toISOString(),
								values: persistedValues,
							},
						});
					}
				}

				const mergedValues = mergeWithCurrentMergeTags(
					defaultTestValues,
					persistedValues,
				);
				const sanitizedMergedValues = sanitizeDownstreamResolvedTestValues(
					mergedValues,
				);
				setTestValues(sanitizedMergedValues);

				const removedDownstreamResolvedValues =
					Object.keys(sanitizedMergedValues).length !==
					Object.keys(mergedValues).length;

				if (kvstoreUserId && removedDownstreamResolvedValues) {
					await kvstore.update({
						component: KVSTORE_COMPONENT_KEY,
						userId: kvstoreUserId,
						data: {
							version: KVSTORE_SCHEMA_VERSION,
							updatedAt: new Date().toISOString(),
							values: sanitizedMergedValues,
						},
					});
				}
			} catch (error) {
				console.error(
					'[PreviewModal] Failed to load test variables from kvstore:',
					error,
				);
				setTestValues(defaultTestValues);
			} finally {
				setHasLoadedTestValues(true);
			}
		};

		loadTestValues();
	}, [defaultTestValues, hasLoadedTestValues, isVisible, kvstoreUserId]);

	// Not gated on isVisible — this component stays mounted for the whole editor session,
	// so fetching on mount usually resolves before Preview is even opened.
	useEffect(() => {
		if (hasLoadedCampaignDisclaimer || isDesignAdmin) return;

		let cancelled = false;

		fetchCampaignDisclaimerOnce().then(disclaimer => {
			if (cancelled) return;
			if (disclaimer) setRealCampaignDisclaimer(disclaimer);
			setHasLoadedCampaignDisclaimer(true);
		});

		return () => {
			cancelled = true;
		};
	}, [hasLoadedCampaignDisclaimer, isDesignAdmin]);

	// Reset modal-only UI state when modal closes. Re-load preferences on next open.
	// hasLoadedCampaignDisclaimer stays true once set — don't reset it here.
	useEffect(() => {
		if (!isVisible) {
			setIsTestVariablesModalVisible(false); // Also close test variables modal if open
			setIsSendTestModalVisible(false);
			setIsHelpPopoverVisible(false);
			setPreviewIframeWindow(null);
			setHasLoadedTestValues(false);
		}
	}, [isVisible]);

	useEffect(() => {
		// Iframe clicks do not bubble to the parent document, so close the help popover here.
		if (!isHelpPopoverVisible || !previewIframeWindow?.document) return;

		const closePopover = () => {
			setIsHelpPopoverVisible(false);
		};

		const iframeDocument = previewIframeWindow.document;
		iframeDocument.addEventListener('mousedown', closePopover, true);
		iframeDocument.addEventListener('touchstart', closePopover, true);

		return () => {
			iframeDocument.removeEventListener('mousedown', closePopover, true);
			iframeDocument.removeEventListener('touchstart', closePopover, true);
		};
	}, [isHelpPopoverVisible, previewIframeWindow]);

	const handlePreviewIframeWindowRef = useCallback((contentWindow: Window) => {
		setPreviewIframeWindow(contentWindow);
	}, []);

	const handleOpenTestVariables = useCallback(() => {
		setIsTestVariablesModalVisible(true);
	}, []);

	const handleOpenSendTest = useCallback(() => {
		setIsSendTestModalVisible(true);
	}, []);

	const handleCloseSendTest = useCallback(() => {
		setIsSendTestModalVisible(false);
	}, []);

	const handleCloseTestVariables = useCallback(() => {
		setIsTestVariablesModalVisible(false);
	}, []);

	const persistTestValues = useCallback(
		async (values: Record<string, string>) => {
			try {
				if (!kvstoreUserId) return;

				const sanitizedValues = sanitizeDownstreamResolvedTestValues(values);

				await kvstore.update({
					component: KVSTORE_COMPONENT_KEY,
					userId: kvstoreUserId,
					data: {
						version: KVSTORE_SCHEMA_VERSION,
						updatedAt: new Date().toISOString(),
						values: sanitizedValues,
					},
				});
			} catch (error) {
				console.error('[PreviewModal] Failed to save test variables to kvstore:', error);
			}
		},
		[kvstoreUserId],
	);

	const handleTestValuesChange = useCallback(
		(values: Record<string, string>) => {
			const sanitizedValues = sanitizeDownstreamResolvedTestValues(values);
			setTestValues(sanitizedValues);
			window.dispatchEvent(
				new CustomEvent(PREVIEW_TEST_VALUES_UPDATED_EVENT, {
					detail: sanitizedValues,
				}),
			);
			void persistTestValues(sanitizedValues);
		},
		[persistTestValues],
	);

	// True reset: show the pristine base values AND wipe the persisted kvstore
	// overrides (plus the live editor overrides) so stale saved test values
	// can't keep masking the real defaults.
	const handleTestValuesReset = useCallback(
		(resetValues: Record<string, string>) => {
			const sanitizedValues = sanitizeDownstreamResolvedTestValues(resetValues);
			setTestValues(sanitizedValues);
			window.dispatchEvent(
				new CustomEvent(PREVIEW_TEST_VALUES_UPDATED_EVENT, {
					detail: {},
				}),
			);
			void persistTestValues({});
		},
		[persistTestValues],
	);

	function resolvePreviewTestValue(
		key: string,
		testData: Record<string, string>,
	): string | undefined {
		if (isDownstreamResolvedMergeTagKey(key)) {
			return (
				resolveDownstreamValueFromMergeTags(key) ||
				getPreviewOnlyDownstreamMergeTagValue(key)
			);
		}

		const rawKey = String(key || '');
		const trimmedKey = rawKey.trim();
		const normalizedRawKey = normalizeMergeTagKey(rawKey);
		const normalizedTrimmedKey = normalizeMergeTagKey(trimmedKey);

		// Recipient-real preview: listing.* answers come from the block-level
		// real-listing resolution only. A tag still unresolved by the time it
		// reaches these passes is one the send cannot fill (no listing bound, or
		// a bound listing whose field is genuinely blank) — it renders blank,
		// never test/sample data, exactly like send-time Handlebars with no
		// listing key. The bare {{url}} alias below is the same tag. Test data
		// still answers listing.* outside recipient preview (Design Manager),
		// and still seeds unselected listing BLOCKS via
		// fillUnselectedBlocksWithMockPreviewData, which reads it directly.
		if (
			recipientPreview &&
			(normalizedTrimmedKey.startsWith('listing.') || normalizedTrimmedKey === 'url')
		) {
			return undefined;
		}

		const resolvedValue =
			testData[rawKey] ??
			testData[trimmedKey] ??
			testData[normalizedRawKey] ??
			testData[normalizedTrimmedKey];

		if (resolvedValue !== undefined) {
			// A genuinely blank value (whitespace-only included) is not a resolved
			// value — return undefined so the caller falls through to the tag's
			// own fallback/placeholder handling, same as an unresolved tag. Never
			// silently substitute sample data for a real-but-blank value.
			const trimmedValue = String(resolvedValue).trim();
			return trimmedValue ? trimmedValue : undefined;
		}

		// Listings/Criteria buttons use a bare `{{url}}` token — alias it to `listing.url`.
		if (normalizedTrimmedKey === 'url') {
			const listingUrlValue = testData['listing.url'];
			if (listingUrlValue !== undefined) {
				// Same rule as above: a real-but-blank aliased value is not a
				// resolved value — return undefined rather than falling through
				// to sample-URL generation below.
				const trimmedListingUrl = String(listingUrlValue).trim();
				return trimmedListingUrl ? trimmedListingUrl : undefined;
			}
		}

		if (!/^[a-zA-Z0-9_.-]+$/.test(trimmedKey)) {
			return undefined;
		}

		const leafKey = trimmedKey.split('.').pop() || trimmedKey;
		return generateSampleValue(leafKey);
	}

	function resolveConditionalMergeTags(
		text: string,
		testData: Record<string, string>,
	): string {
		if (!text) return text;

		const normalizeTemplateSyntax = (value: string) =>
			String(value || '')
				.replace(/&#123;|&#x7b;|&lbrace;|&lcub;/gi, '{')
				.replace(/&#125;|&#x7d;|&rbrace;|&rcub;/gi, '}');

		const resolveBranchMergeTags = (branchContent: string): string =>
			normalizeTemplateSyntax(branchContent).replace(
				/\{\{([^}]+)\}\}/g,
				(match, rawKey) => {
					const trimmed = String(rawKey || '').trim();
					if (
						trimmed.startsWith('#') ||
						trimmed.startsWith('/') ||
						trimmed === 'else'
					) {
						return '';
					}

					const parsedHelper = parseDynamicTextHelperExpression(trimmed);
					const pipeIdx = trimmed.indexOf('|');
					const key = parsedHelper
						? parsedHelper.key
						: pipeIdx >= 0
							? trimmed.slice(0, pipeIdx).trim()
							: trimmed;
					const fallback =
						!parsedHelper && pipeIdx >= 0 ? trimmed.slice(pipeIdx + 1).trim() : '';
					const value = resolvePreviewTestValue(key, testData);
					if (value !== undefined && value !== '') {
						return parsedHelper
							? formatDynamicTextValue(value, parsedHelper.format, parsedHelper.maxLength)
							: String(value);
					}
					if (fallback) return fallback;
					// A user.* field with nothing to show and no fallback configured
					// renders blank -- a real recipient would never see the raw tag
					// either, so Preview shouldn't leak it. Recipient-real preview
					// extends the same rule to every tag: at send time Handlebars
					// resolves a missing key to "", never the raw token.
					if (recipientPreview || key.startsWith('user.')) return '';
					return match;
				},
			);

		const resolveConditionalBlocks = (content: string): string => {
			const normalizedContent = normalizeTemplateSyntax(content);
			const tokenPattern = /\{\{\s*(#if\s+([\w.-]+)|else|\/if)\s*\}\}/g;
			let output = '';
			let cursor = 0;
			let startMatch = tokenPattern.exec(normalizedContent);

			while (startMatch) {
				const token = String(startMatch[1] || '').trim();
				if (!token.startsWith('#if ')) {
					startMatch = tokenPattern.exec(normalizedContent);
					continue;
				}

				const conditionKey = String(startMatch[2] || '').trim();
				const blockStartIndex = startMatch.index;
				const trueBranchStart = tokenPattern.lastIndex;
				let depth = 1;
				let elseMatch: RegExpExecArray | null = null;
				let endMatch: RegExpExecArray | null = null;
				let nextMatch = tokenPattern.exec(normalizedContent);

				while (nextMatch) {
					const nextToken = String(nextMatch[1] || '').trim();
					if (nextToken.startsWith('#if ')) {
						depth += 1;
					} else if (nextToken === '/if') {
						depth -= 1;
						if (depth === 0) {
							endMatch = nextMatch;
							break;
						}
					} else if (nextToken === 'else' && depth === 1) {
						elseMatch = nextMatch;
					}

					nextMatch = tokenPattern.exec(normalizedContent);
				}

				if (!endMatch) {
					break;
				}

				const elseStart = elseMatch?.index ?? endMatch.index;
				const fallbackStart = elseMatch ? elseMatch.index + elseMatch[0].length : endMatch.index;
				const trueBranch = normalizedContent.slice(trueBranchStart, elseStart);
				const fallbackBranch = normalizedContent.slice(fallbackStart, endMatch.index);
				const value = resolvePreviewTestValue(conditionKey, testData);
				const selectedBranch = value !== undefined && value !== '' ? trueBranch : fallbackBranch;

				output += normalizedContent.slice(cursor, blockStartIndex);
				output += resolveBranchMergeTags(resolveConditionalBlocks(selectedBranch).trim());
				cursor = endMatch.index + endMatch[0].length;
				tokenPattern.lastIndex = cursor;
				startMatch = tokenPattern.exec(normalizedContent);
			}

			return output + normalizedContent.slice(cursor);
		};

		return resolveConditionalBlocks(text);
	}

	function replaceMergeTags(text: string, testData: Record<string, string>): string {
		if (!text) return text;

		const preProcessed = resolveConditionalMergeTags(text, testData);

		return preProcessed.replace(/\{\{([^}]+)\}\}/g, (match, rawKey) => {
			const trimmed = rawKey.trim();
			if (trimmed.startsWith('#') || trimmed.startsWith('/') || trimmed === 'else')
				return '';
			const parsedHelper = parseDynamicTextHelperExpression(trimmed);
			const pipeIdx = trimmed.indexOf('|');
			const key = parsedHelper
				? parsedHelper.key
				: pipeIdx >= 0
					? trimmed.slice(0, pipeIdx).trim()
					: trimmed;
			const fallback =
				!parsedHelper && pipeIdx >= 0 ? trimmed.slice(pipeIdx + 1).trim() : '';
			const value = resolvePreviewTestValue(key, testData);
			if (value !== undefined && value !== '') {
				return parsedHelper
					? formatDynamicTextValue(value, parsedHelper.format, parsedHelper.maxLength)
					: String(value);
			}
			if (fallback) return fallback;
			// A user.* field with nothing to show and no fallback configured
			// renders blank -- a real recipient would never see the raw tag
			// either, so Preview shouldn't leak it.
			if (key.startsWith('user.')) return '';
			return match;
		});
	}

	// Generate custom preview with test values or defaults
	const customPreviewData = useMemo(() => {
		// Use test values if available, otherwise use default test values.
		// Always overlay the user's REAL dynamic brand colours from the merge tags so
		// {{user.brandPrimaryColor}} etc. resolve to the same hex the canvas shows. They
		// aren't hand-editable test variables, so persisted testValues never carry them;
		// overlaying here keeps every downstream pass (dataSource + regex) consistent and
		// prevents the raw tag from rendering black.
		const baseTestValues =
			Object.keys(testValues).length > 0 ? testValues : defaultTestValues;
		const brandColorOverrides = getBrandColorOverridesFromMergeTags(previewMergeTags);
		const activeTestValues: Record<string, string> = { ...baseTestValues };
		if (brandColorOverrides?.user.brandPrimaryColor) {
			activeTestValues['user.brandPrimaryColor'] =
				brandColorOverrides.user.brandPrimaryColor;
		}
		if (brandColorOverrides?.user.brandSecondaryColor) {
			activeTestValues['user.brandSecondaryColor'] =
				brandColorOverrides.user.brandSecondaryColor;
		}
		// The signed-in account's real profile fields overlay test variables for the
		// same reason the brand colours do: they are facts about the sender, not
		// sample data to play with. The compliance footer prints the licensee's name,
		// license number, brokerage and office address — previewing those against
		// edited test values would show the user a footer that will never be sent.
		// Design Manager passes nothing here and keeps the sample data.
		// A genuinely blank real field is overlaid too (as '') rather than skipped —
		// skipping it would leave whatever sample value already sits at that key in
		// activeTestValues untouched, fabricating a footer detail for a real field
		// that's actually empty. The downstream resolvers already treat '' the same
		// as unresolved and fall through to the tag's own fallback/placeholder.
		if (realUserProfile) {
			Object.keys(realUserProfile).forEach(leafKey => {
				activeTestValues[`user.${leafKey}`] = String(
					realUserProfile[leafKey] ?? '',
				).trim();
			});
		}
		const previewUnsubscribeLink = resolvePreviewTestValue(
			'unsubscribeLink',
			activeTestValues,
		);
		const previewCampaignDisclaimer = resolvePreviewTestValue(
			'campaignDisclaimer',
			activeTestValues,
		);
		// Seed downstream values so block-level dynamic bindings resolve in the render pass,
		// not only the post-render regex pass.
		const previewOnlyLegalData = {
			...(previewUnsubscribeLink
				? { unsubscribeLink: previewUnsubscribeLink }
				: {}),
			...(isUsableDownstreamPreviewValue(previewCampaignDisclaimer)
				? { campaignDisclaimer: previewCampaignDisclaimer }
				: {}),
		};
		const previewOnlyDownstreamData =
			Object.keys(previewOnlyLegalData).length > 0
				? {
						...previewOnlyLegalData,
						legal_compliance: { ...previewOnlyLegalData },
					}
				: {};

		try {
			// Convert flat test values to nested structure for MJML processing
			const nestedTestData: Record<string, any> = {};
			Object.entries(activeTestValues).forEach(([key, value]) => {
				const parts = key.split('.');
				if (parts.length === 2) {
					const [groupKey, fieldKey] = parts;
					if (!nestedTestData[groupKey]) {
						nestedTestData[groupKey] = {};
					}
					nestedTestData[groupKey][fieldKey] = value;
				}
			});

			// Clone page data and adjust breakpoint for preview
			const breakpoint = parseInt(pageData.data.value.breakpoint || '0');
			let adjustBreakPoint = breakpoint;
			if (breakpoint > 360) {
				adjustBreakPoint = Math.max(viewMode === 'mobile' ? 320 : 1200, breakpoint);
			}

			const cloneData = {
				...pageData,
				data: {
					...pageData.data,
					value: {
						...pageData.data.value,
						breakpoint: adjustBreakPoint + 'px',
					},
				},
			};

			// Fill Listings/SingleListing/Criteria blocks that have no real selection yet
			// with sample data derived from activeTestValues — the same effective test data
			// (kvstore overrides -> defaults) used for every other merge-tag substitution below —
			// so editing/resetting test variables updates these generated items too, instead of
			// being stuck on a static baseMergeTags snapshot. Blocks that already have a real
			// selection are untouched. Design admins are excluded and keep seeing the block's own
			// built-in placeholder (the pre-existing behaviour).
			const previewCloneData = isDesignAdmin
				? cloneData
				: fillUnselectedBlocksWithMockPreviewData(cloneData, activeTestValues);

			// Mock signature HTML for preview
			const mockSignatureHtml = `
        <table cellpadding="0" cellspacing="0" border="0" class="signature-container" style="width: 100%; border-collapse: collapse;">
          <tr>
            <td class="signature-photo" style="width: 160px; vertical-align: top; padding: 20px;">
              <img
                src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=faces"
                width="140"
                height="140"
                style="display: block; width: 140px; height: 140px; border-radius: 4px;"
                alt="Michael Chen"
              />
            </td>
            <td class="signature-info" style="vertical-align: top; padding: 20px;">
              <div style="font-family: Arial, sans-serif;">
                <div style="font-size: 20px; font-weight: bold; color: #1a1a1a; margin-bottom: 4px;">Michael Chen</div>
                <div style="font-size: 13px; color: #666666; margin-bottom: 12px;">LIC #987654</div>
                <div style="font-size: 14px; color: #333333; line-height: 1.6; margin-bottom: 12px;">
                  <div style="margin-bottom: 4px;"><span style="color: #888888;">Phone:</span> (555) 012-3456</div>
                  <div style="margin-bottom: 4px;"><a href="mailto:michael.chen@beaconrealestate.com" style="color: #0066cc; text-decoration: none;">michael.chen@beaconrealestate.com</a></div>
                  <div style="margin-bottom: 4px;"><a href="https://www.beaconrealestate.example.com/michaelchen" style="color: #0066cc; text-decoration: none;">beaconrealestate.example.com</a></div>
                  <div style="color: #666666; font-size: 13px;">456 Commerce Plaza, Anytown, TX 12345</div>
                </div>
              </div>
            </td>
            <td class="social-info" style="width: 120px; vertical-align: top; padding: 20px;">
              <div style="margin-top: 12px;">
                <img
				  src="${SIGNATURE_BLOCK_LOGO_URL}"
                  style="margin-right: 8px; vertical-align: middle; max-width: 100%; height: 32px;"
                  alt="Brokerage Logo"
                />
              </div>
              <div style="margin-top: 12px;">
                <a href="#" style="display: inline-block; margin-right: 8px;">
                  <img src="https://bay.cdn.bkat.io/smc/facebook-icon.png" width="20" height="20" style="display: block;" alt="Facebook" />
                </a>
                <a href="#" style="display: inline-block; margin-right: 8px;">
                  <img src="https://bay.cdn.bkat.io/smc/linkedin-icon.png" width="20" height="20" style="display: block;" alt="LinkedIn" />
                </a>
                <a href="#" style="display: inline-block; margin-right: 8px;">
                  <img src="https://bay.cdn.bkat.io/smc/twitter-logo-gray.png" width="20" height="20" style="display: block;" alt="X" />
                </a>
                <a href="#" style="display: inline-block;">
                  <img src="https://bay.cdn.bkat.io/smc/instagram-icon.png" width="20" height="20" style="display: block;" alt="Instagram" />
                </a>
              </div>
            </td>
          </tr>
        </table>
        <style type="text/css">
          @media only screen and (max-width: 480px) {
            .signature-container tr {
              display: block !important;
            }
            .signature-photo,
            .signature-info,
            .social-info {
              display: block !important;
              width: 100% !important;
              text-align: center !important;
              padding: 15px !important;
              box-sizing: border-box !important;
            }
            .signature-photo img {
              margin: 0 auto !important;
            }
            .signature-info > div,
            .social-info {
              text-align: center !important;
            }
          }
        </style>
      `;

			// Generate HTML with test values.
			// Use testing mode so blocks render maxCount items with sample data, and so
			// dynamic brand colours ({{user.brandPrimaryColor}} etc.) resolve from the
			// dataSource. nestedTestData already carries the real brand hex because
			// activeTestValues was overlaid with it above.
			let mjmlString = JsonToMjml({
				data: previewCloneData,
				mode: 'testing',
				context: previewCloneData,
				dataSource: {
					...cloneDeep(previewOnlyDownstreamData),
					...cloneDeep(nestedTestData),
					previewMode: true,
					// Which app is previewing. Design Manager runs as design admin and
					// wants the editor's empty-state chrome in preview; ClientBAY does
					// not — its preview promises what the recipient receives. Blocks
					// already read dataSource.isDesignAdmin on the canvas; the preview
					// simply never passed it, so they could not tell the two apart.
					isDesignAdmin: Boolean(isDesignAdmin),
					// Blocks read this to render an unresolved dynamic field with no
					// fallback as blank (a dynamic image collapses) instead of showing
					// editor chrome — the recipient-real preview.
					...(recipientPreview ? { recipientPreview: true } : {}),
					AgentEmailSignature:
						realUserEmailSignature !== undefined ? realUserEmailSignature : mockSignatureHtml,
				},
			});

			mjmlString = resolveConditionalMergeTags(mjmlString, activeTestValues);
			// Main pass: resolve remaining {{key}} tags
			mjmlString = mjmlString.replace(
				/\{\{([^}]+)\}\}/g,
				(match: string, rawKey: string) => {
					const trimmed = rawKey.trim();
					// Skip orphaned Handlebars block tokens
					if (trimmed.startsWith('#') || trimmed.startsWith('/') || trimmed === 'else')
						return '';
					const parsedHelper = parseDynamicTextHelperExpression(trimmed);
					const pipeIdx = trimmed.indexOf('|');
					const key = parsedHelper
						? parsedHelper.key
						: pipeIdx >= 0
							? trimmed.slice(0, pipeIdx).trim()
							: trimmed;
					const fallback =
						!parsedHelper && pipeIdx >= 0 ? trimmed.slice(pipeIdx + 1).trim() : '';
					const value = resolvePreviewTestValue(key, activeTestValues);
					if (value !== undefined && value !== '') {
						return parsedHelper
							? formatDynamicTextValue(value, parsedHelper.format, parsedHelper.maxLength)
							: String(value);
					}
					if (fallback) return fallback;
					// A user.* field with nothing to show and no fallback configured
					// renders blank -- a real recipient would never see the raw tag
					// either, so Preview shouldn't leak it. Recipient-real preview
					// extends the same rule to every tag: at send time Handlebars
					// resolves a missing key to "", never the raw token.
					if (recipientPreview || key.startsWith('user.')) return '';
					return match;
				},
			);

			let html = mjml(mjmlString).html;
			if (isSingleHtmlBlockContent(previewCloneData)) {
				html = stripMjmlDefaultHeadReset(html);
			}
			html = enforceInheritedColorOnUnsubscribeAnchors(html);
			// ClientBAY preview never shows the drop hint. getPlaceholder already
			// suppresses it at the render layer; this removes it from the final HTML
			// as well, so the modal cannot show one no matter which pipeline produced
			// the string. Gated on isDesignAdmin — the same variable this component
			// already branches preview behavior on — not on a threaded flag.
			if (!isDesignAdmin && html.includes('<svg')) {
				try {
					const doc = new DOMParser().parseFromString(html, 'text/html');
					let removed = 0;
					// The hint and nothing else: a 300-wide svg holding the r="35" circle.
					doc.querySelectorAll('svg[width="300"] circle[r="35"]').forEach(circle => {
						const svg = circle.closest('svg');
						if (!svg) return;
						(svg.closest('td')?.closest('table') || svg.closest('td') || svg).remove();
						removed += 1;
					});
					if (removed) html = `<!doctype html>${doc.documentElement.outerHTML}`;
				} catch {
					// A parse failure must never cost the user their preview.
				}
			}

			// Inject preview text (subTitle) as hidden preheader if it exists
			const subTitle = replaceMergeTags(
				formState.values.subTitle || '',
				activeTestValues,
			);
			if (subTitle) {
				const preheaderDiv = `<div style="display:none;font-size:1px;color:#ffffff;line-height:1px;max-height:0px;max-width:0px;opacity:0;overflow:hidden;">${subTitle}</div>`;
				// Insert preheader right after <body> tag
				html = html.replace(/<body([^>]*)>/, `<body$1>${preheaderDiv}`);
			}

			return {
				html,
				errMsg: null,
			};
		} catch (error: any) {
			return {
				html: '',
				errMsg: error?.message || 'Preview generation failed',
			};
		}
	}, [
		testValues,
		defaultTestValues,
		previewMergeTags,
		realUserProfile,
		realUserEmailSignature,
		pageData,
		viewMode,
		formState.values.subTitle,
		isDesignAdmin,
		recipientPreview,
	]);

	const previewIframeHtml = useMemo(() => {
		return buildPreviewIframeHtml(customPreviewData?.html || '', viewMode);
	}, [customPreviewData?.html, viewMode]);

	// Use test values if available, otherwise use default test values
	const activeTestValues =
		Object.keys(testValues).length > 0 ? testValues : defaultTestValues;

	const subject = replaceMergeTags(
		formState.values.subject || 'Email Preview',
		activeTestValues,
	);
	const subTitle = replaceMergeTags(formState.values.subTitle || '', activeTestValues);

	// Wait for the real disclaimer so the raw {{campaignDisclaimer}} tag never flashes on screen.
	const isDisclaimerReady = isDesignAdmin || hasLoadedCampaignDisclaimer;

	let modalBackdropClassName = clsx([
		'preview-modal-backdrop',
		isVisible ? 'preview-modal-backdrop--visible' : '',
	]);
	let modalClassName = clsx(['preview-modal', isVisible ? 'preview-modal--visible' : '']);
	const previewHelpArticleUrl = isDesignAdmin
		? 'https://www.iqsupport.me/portal/en/kb/articles/coming-soon-new-preview-mode-for-your-design-templates'
		: 'https://www.iqsupport.me/portal/en/kb/articles/preview-mode-for-your-email-templates';

	const previewHelpText = isDesignAdmin ? (
		<>
			<span>
				We've filled in any dynamic fields using test data to help you visualize your design!
			</span>
			<div>
				<div className='preview-modal__edit-test-variables'>Want to customize the test data? </div>
				Click <span className='preview-modal__edit-test-variables'>‘Edit Test Variables’</span> to change the test data and see how different names or images fit your design. You can also clear a field entirely to test its fallback values. We'll even save your custom test data to use across all your future previews!
			</div>
		</>
	) : (
		<>
			<span>Here is how we fill in the dynamic fields to help you visualize your design:</span>
			<ul className='preview-modal__edit-test-variables-list'>
				<li>
					<span className='preview-modal__edit-test-variables'>Your Personal Data: </span>
					<span>We use your real profile information. If any of your details are empty, you'll see the template's default fallback values. </span>
					<span className='preview-modal__notes'>(Need to update your info? Head over to your profile!)</span>
				</li>
				<li>
					<span className='preview-modal__edit-test-variables'>Recipient Data: </span>
					Since we don't know who you are sending this to yet, we use test data to show you how your recipients' information will look.
				</li>
				<li>
					<span className='preview-modal__edit-test-variables'>Listing Data: </span>
					For any blocks where you've selected real properties, you'll see the actual listing data here. For unmapped properties, we use test data to fill in the blanks.
				</li>
			</ul>
			<div>
				<div className='preview-modal__edit-test-variables'>Want to customize the test data? </div>
				Click <span className='preview-modal__edit-test-variables'>‘Edit Test Variables’</span> to change the test data for your listings or recipients and see how different names or images fit your design. You can also clear a field entirely to test its fallback values. We'll even save your custom test data to use across all your future previews!
			</div>
		</>
	);

	return createPortal(
		<>
			{/* Backdrop */}
			<div
				className={modalBackdropClassName}
				onClick={onClose}
			/>

			{/* Modal */}
			<div className={modalClassName}>
				{/* Header */}
				<div className='preview-modal__header'>
					<div className='preview-modal__header-left'>
						<h3 className='preview-modal__title'>Preview Mode</h3>
						<div className='preview-modal__divider' />
						<OverlayTrigger
							show={isHelpPopoverVisible}
							onToggle={(nextShown: boolean) => setIsHelpPopoverVisible(nextShown)}
							overlay={(
								<Popover
									id='popover-basic'
									className='preview-modal__popover-help'
								>
									<Popover.Body>
										{previewHelpText}
										<span className='preview-modal__notes'>(Note: This preview automatically applies any specific formatting or max-length limits assigned to your dynamic fields).</span>
										<a
											className='preview-modal__subtitle'
											href={previewHelpArticleUrl}
											target='_blank'
											rel='noopener noreferrer'
										>
											Learn More
										</a>
									</Popover.Body>
								</Popover>
							)}
							placement='bottom'
							rootClose={true}
							trigger='click'
						>
							<button
								type='button'
								className='preview-modal__subtitle'
							>
								How does this work?
							</button>
						</OverlayTrigger>
					</div>

					<div className='preview-modal__header-center'>
						<div className='preview-modal__view-toggle'>
							<div
								className={clsx('preview-modal__toggle-btn', {
									'preview-modal__toggle-btn--active': viewMode === 'desktop',
								})}
							>
								<Button
									noBorder
									onClick={() => setViewMode('desktop')}
								>
									<BsDisplay size={16} />
									<span>Desktop</span>
								</Button>
							</div>
							<div
								className={clsx('preview-modal__toggle-btn', {
									'preview-modal__toggle-btn--active': viewMode === 'mobile',
								})}
							>
								<Button
									noBorder
									onClick={() => setViewMode('mobile')}
								>
									<BsPhone size={16} />
									<span>Mobile</span>
								</Button>
							</div>
						</div>
					</div>

					<div className='preview-modal__header-right'>
						<div className='preview-modal__action-btn'>
							<Button
								noBorder
								onClick={handleOpenTestVariables}
							>
								<span>Edit Test Variables</span>
							</Button>
						</div>
						{/* Send Test hidden in block editor variant — a block is not a
						   standalone sendable email (no subject/preview). */}
						{!blockMode && (
							<div className='preview-modal__action-btn preview-modal__action-btn--send-test'>
								<Button
									noBorder
									onClick={handleOpenSendTest}
								>
									<BsSendFill size={16} />
									<span>Send Test</span>
								</Button>
							</div>
						)}
						<div className='preview-modal__done-btn'>
							<Button onClick={onClose}>{isDesignAdmin ? 'Done' : 'Back to Editing'}</Button>
						</div>
					</div>
				</div>

				{/* Subject Line (hidden in block editor variant — a block has no subject) */}
				{!blockMode && (
					<div className='preview-modal__subject'>
						<div className='preview-modal__subject-content'>
							<span className='preview-modal__subject-label'>Subject:</span>
							<div className='preview-modal__subject-text-wrapper'>
								<span>{subject}</span>
							</div>
						</div>
					</div>
				)}

				{/* Subtitle/Preview Text (hidden in block editor variant) */}
				{!blockMode && subTitle && (
					<div className='preview-modal__subject preview-modal__subject--subtitle'>
						<div className='preview-modal__subject-content'>
							<span className='preview-modal__subject-label'>Preview Text:</span>
							<div className='preview-modal__subject-text-wrapper'>
								<span>{subTitle}</span>
							</div>
						</div>
					</div>
				)}

				{/* Content */}
				<div className='preview-modal__content'>
					{!isDisclaimerReady ? (
						<div style={{ padding: '40px 0' }}>
							<BaySpinner title='Loading preview...' />
						</div>
					) : customPreviewData?.errMsg ? (
						<div style={{ textAlign: 'center', fontSize: 24, color: 'red' }}>
							<>{customPreviewData?.errMsg}</>
						</div>
					) : (
						<div
							className={clsx('preview-modal__viewport', {
								'preview-modal__viewport--mobile': viewMode === 'mobile',
							})}
						>
							<SyncScrollIframeComponent
								isActive={true}
								windowRef={handlePreviewIframeWindowRef}
								srcDoc={previewIframeHtml}
								style={{
									border: 'none',
									height: '100%',
									width: '100%',
									overflow: 'auto',
									WebkitOverflowScrolling: 'touch',
								}}
							>
								<></>
							</SyncScrollIframeComponent>
						</div>
					)}
				</div>
			</div>

			{/* Test Variables Modal */}
			<TestVariablesModal
				isVisible={isTestVariablesModalVisible}
				onClose={handleCloseTestVariables}
				mergeTags={previewMergeTags || {}}
				baseMergeTags={resetMergeTags || {}}
				testValues={testValues}
				onTestValuesChange={handleTestValuesChange}
				onTestValuesReset={handleTestValuesReset}
				hideUserFields={recipientPreview}
			/>

			{/* Send Test Email Modal */}
			<SendTestEmailModal
				isVisible={isSendTestModalVisible}
				onClose={handleCloseSendTest}
				htmlContent={customPreviewData?.html || ''}
				subject={subject}
				onSendTestEmail={onSendTestEmail}
				userEmail={userEmail}
				userName={userName}
			/>
		</>,
		document.body,
	);
}
